<template>
  <div class="admin-home">
    <h1>Homepage Management</h1>
    
    <!-- Slider Management -->
    <div class="section">
      <h2>Hero Slider</h2>
      <div class="slider-management">
        <div class="slider-list">
          <div v-for="(slide, index) in slides" :key="index" class="slider-item">
            <div class="image-preview">
              <img v-if="slide.image" :src="slide.image" :alt="slide.title">
              <div v-else class="no-image">No image</div>
            </div>
            <div class="slider-info">
              <input v-model="slide.title" placeholder="Slide Title">
              <input v-model="slide.description" placeholder="Slide Description">
              <input v-model="slide.link" placeholder="Slide Link">
              <input type="file" @change="(e) => handleImageUpload(e, 'slide', index)" accept="image/*">
            </div>
            <button @click="removeSlide(index)" class="delete-btn">Delete</button>
          </div>
        </div>
        <button @click="addSlide" class="add-btn">Add New Slide</button>
      </div>
    </div>

    <!-- Product Carousel Management -->
    <div class="section">
      <h2>Product Carousel</h2>
      <p class="upload-guidance">
        Recommended image spec: <strong>4:5 ratio</strong> (portrait),
        <strong>1000x1250 px or above</strong>, and file size <strong>up to 5MB</strong> per image.
      </p>
      <div class="carousel-settings-row">
        <label class="switch-label">
          <input type="checkbox" v-model="productCarousel.enabled">
          <span>Enabled on homepage</span>
        </label>
        <label class="speed-label">
          <span>Speed (px/sec)</span>
          <input type="number" min="10" max="200" step="1" v-model.number="productCarousel.speedPxPerSec">
        </label>
      </div>

      <div class="carousel-management">
        <div class="carousel-list">
          <div v-for="(item, index) in productCarousel.items" :key="`carousel-item-${index}`" class="carousel-item">
            <div class="image-preview">
              <img v-if="item.image" :src="item.image" :alt="item.title || `Product ${index + 1}`">
              <div v-else class="no-image">No image</div>
            </div>
            <div class="carousel-info">
              <div class="category-meta-row">
                <span class="category-order-chip">Position {{ index + 1 }}</span>
                <div class="reorder-actions">
                  <button
                    type="button"
                    class="reorder-btn"
                    :disabled="index === 0"
                    @click="moveCarouselProduct(index, -1)"
                  >
                    Move Up
                  </button>
                  <button
                    type="button"
                    class="reorder-btn"
                    :disabled="index === productCarousel.items.length - 1"
                    @click="moveCarouselProduct(index, 1)"
                  >
                    Move Down
                  </button>
                </div>
              </div>
              <input v-model="item.title" placeholder="Product Title">
              <input v-model="item.price" placeholder="Price (e.g. $79.00 USD)">
              <input v-model="item.link" placeholder="Product Link">
              <input type="file" @change="(e) => handleImageUpload(e, 'productCarousel', index)" accept="image/*">
            </div>
            <button @click="removeCarouselProduct(index)" class="delete-btn">Delete</button>
          </div>
        </div>
        <div class="carousel-add-actions">
          <button @click="addCarouselProductToStart" class="add-btn add-btn--start">+ Add to Start</button>
          <button @click="addCarouselProductToEnd" class="add-btn">+ Add to End</button>
        </div>
      </div>
    </div>

    <!-- Categories Management -->
    <div class="section">
      <h2>Categories</h2>
      <div class="categories-management">
        <div class="category-list">
          <div v-for="(category, index) in categories" :key="index" class="category-item">
            <div class="image-preview">
              <img v-if="category.image" :src="category.image" :alt="category.name">
              <div v-else class="no-image">No image</div>
            </div>
            <div class="category-info">
              <div class="category-meta-row">
                <span class="category-order-chip">Position {{ index + 1 }}</span>
                <div class="reorder-actions">
                  <button
                    type="button"
                    class="reorder-btn"
                    :disabled="index === 0"
                    @click="moveCategory(index, -1)"
                  >
                    Move Up
                  </button>
                  <button
                    type="button"
                    class="reorder-btn"
                    :disabled="index === categories.length - 1"
                    @click="moveCategory(index, 1)"
                  >
                    Move Down
                  </button>
                </div>
              </div>
              <input v-model="category.name" placeholder="Category Name">
              <input v-model="category.description" placeholder="Category Description">
              <input type="file" @change="(e) => handleImageUpload(e, 'category', index)" accept="image/*">
              <label class="hidden-toggle">
                <input type="checkbox" v-model="category.hidden">
                <span>Hidden (URL only, not shown on homepage)</span>
              </label>
            </div>
            <button @click="removeCategory(index)" class="delete-btn">Delete</button>
          </div>
        </div>
        <button @click="addCategory" class="add-btn">Add New Category</button>
      </div>
    </div>

    <!-- Non translation words management -->
    <div class="section">
      <h2>Non Translation Words (CSV)</h2>
      <p style="margin: 8px 0; color: #666;">Comma or newline separated. One brand/word per entry.</p>
      <textarea v-model="wordsCsv" class="csv-textarea" :placeholder="wordsLoading ? 'Loading...' : 'Enter words here'" :disabled="wordsLoading" />
      <div style="margin-top: 10px; display: flex; gap: 10px; align-items: center;">
        <button @click="saveWords" class="save-btn" :disabled="saveWordsLoading || wordsLoading">
          {{ saveWordsLoading ? 'Saving...' : 'Save Non Translation Words' }}
        </button>
        <button @click="reloadWords" :disabled="wordsLoading" style="background:#6c757d;">Reload</button>
      </div>
    </div>

    <!-- Pricing Rules -->
    <div class="section">
      <h2>Pricing Rules</h2>
      <p style="margin: 8px 0; color: #666;">
        Final price (€) = base price × (1 + markup %) + flat fee.
        Base price = Xianyu price (¥) × exchange rate, in euros; tiers are matched on the base price.
        Leave the last tier's upper limit empty (no limit).
        Products already saved in the database keep their stored price until re-scraped.
      </p>
      <div v-if="pricingLoading" style="color: #666;">Loading...</div>
      <div v-else-if="pricing">
        <label class="speed-label" style="margin-bottom: 12px;">
          <span>Flat fee (€)</span>
          <input type="number" min="0" step="0.01" v-model.number="pricing.flatFeeEur">
        </label>
        <table style="width: 100%; max-width: 640px; border-collapse: collapse; font-size: 0.9rem;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th style="padding: 8px; text-align: left;">Base price from (€)</th>
              <th style="padding: 8px; text-align: left;">Up to (€)</th>
              <th style="padding: 8px; text-align: left;">Markup (%)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(tier, index) in pricing.tiers" :key="`tier-${index}`" style="border-top: 1px solid #eee;">
              <td style="padding: 6px 8px; color: #666;">
                {{ index === 0 ? '0' : `above ${pricing.tiers[index - 1].maxEur || '?'}` }}
              </td>
              <td style="padding: 6px 8px;">
                <input type="number" min="0" step="0.01" v-model="tier.maxEur" :placeholder="index === pricing.tiers.length - 1 ? 'no limit' : ''" style="width: 110px;">
              </td>
              <td style="padding: 6px 8px;"><input type="number" min="0" step="0.1" v-model.number="tier.markupPercent" style="width: 90px;"></td>
              <td style="padding: 6px 8px;">
                <button type="button" class="delete-btn" :disabled="pricing.tiers.length === 1" @click="pricing.tiers.splice(index, 1)">Remove</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div style="margin-top: 10px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
          <button type="button" class="add-btn" @click="addPricingTier">+ Add Tier</button>
          <button type="button" class="save-btn" :disabled="pricingSaving" @click="savePricing">
            {{ pricingSaving ? 'Saving...' : 'Save Pricing Rules' }}
          </button>
          <button type="button" style="background:#6c757d;" @click="resetPricingToDefaults">Reset to defaults</button>
          <span v-if="pricingMessage" :style="{ color: pricingError ? '#dc3545' : '#28a745', fontWeight: 500 }">{{ pricingMessage }}</span>
        </div>
      </div>
    </div>

    <!-- Session Management -->
    <div class="section">
      <h2>Session Management</h2>

      <!-- Status panel -->
      <div style="margin-bottom: 12px;">
        <button @click="loadSessionStatus" class="save-btn" :disabled="sessionLoading" style="background: #6c757d;">
          {{ sessionLoading ? 'Loading...' : 'Refresh Status' }}
        </button>
        <span v-if="sessionStatus" style="margin-left: 12px; font-weight: 500;">
          {{ sessionStatus.totalSessions }} session(s) in pool
        </span>
      </div>

      <div v-if="sessionStatus && sessionStatus.sessions" style="margin-bottom: 16px; border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th style="padding: 8px 12px; text-align: left;">Session ID</th>
              <th style="padding: 8px 12px; text-align: left;">Valid</th>
              <th style="padding: 8px 12px; text-align: left;">Created</th>
              <th style="padding: 8px 12px; text-align: left;">Expires</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(s, id) in sessionStatus.sessions" :key="id" style="border-top: 1px solid #eee;">
              <td style="padding: 8px 12px;">{{ id }}</td>
              <td style="padding: 8px 12px;">
                <span :style="{ color: s.valid ? '#28a745' : '#dc3545', fontWeight: 500 }">
                  {{ s.valid ? '✓ Valid' : '✗ Expired' }}
                </span>
              </td>
              <td style="padding: 8px 12px;">{{ new Date(s.createdAt).toLocaleTimeString() }}</td>
              <td style="padding: 8px 12px;">{{ new Date(s.expiresAt).toLocaleTimeString() }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Validate -->
      <div style="margin-bottom: 12px;">
        <button @click="validateSession" class="save-btn" :disabled="sessionLoading" style="background: #17a2b8;">
          {{ sessionLoading ? 'Checking...' : 'Validate Session' }}
        </button>
        <span v-if="sessionValidateResult" style="margin-left: 12px; font-weight: 500;" :style="{ color: sessionValidateResult.ok ? '#28a745' : '#dc3545' }">
          {{ sessionValidateResult.ok ? '✓ Session alive' : '✗ Login required — ' + (sessionValidateResult.reason || '') }}
        </span>
      </div>

      <!-- Action buttons -->
      <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
        <button @click="sessionAction('rotate', 'Rotate all sessions')" class="save-btn" :disabled="sessionActionStatus === 'loading'" style="background: #fd7e14;">
          {{ sessionActionStatus === 'loading' ? 'Working...' : 'Rotate All Sessions' }}
        </button>
        <button @click="sessionAction('cleanup', 'Cleanup expired sessions')" class="save-btn" :disabled="sessionActionStatus === 'loading'" style="background: #6c757d;">
          Cleanup Expired
        </button>
        <button @click="sessionAction('add', 'Add session')" class="save-btn" :disabled="sessionActionStatus === 'loading'" style="background: #28a745;">
          + Add Session
        </button>
        <span v-if="sessionActionStatus === 'success'" style="color: #28a745; font-weight: 500;">{{ sessionActionMessage }}</span>
        <span v-if="sessionActionStatus === 'error'" style="color: #dc3545; font-weight: 500;">{{ sessionActionMessage }}</span>
      </div>
    </div>

    <!-- Cache Management -->
    <div class="section">
      <h2>Cache Management</h2>
      <div style="display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
        <button @click="clearCategoryCache" class="save-btn" :disabled="cacheClearStatus === 'loading'" style="background: #dc3545;">
          {{ cacheClearStatus === 'loading' ? 'Clearing...' : 'Clear Category Cache' }}
        </button>
        <span v-if="cacheClearStatus === 'success'" style="color: #28a745; font-weight: 500;">{{ cacheClearMessage }}</span>
        <span v-if="cacheClearStatus === 'error'" style="color: #dc3545; font-weight: 500;">{{ cacheClearMessage }}</span>
      </div>
    </div>

    <button @click="saveChanges" class="save-btn" :disabled="loading">
      {{ loading ? 'Saving...' : 'Save All Changes' }}
    </button>
  </div>
</template>

<script>
import config from '../config';

export default {
  name: 'AdminHome',
  data() {
    return {
      slides: [],
      categories: [],
      productCarousel: {
        enabled: false,
        speedPxPerSec: 55,
        items: []
      },
      loading: false,
      wordsCsv: '',
      wordsLoading: false,
      saveWordsLoading: false,
      cacheClearStatus: null,
      cacheClearMessage: '',
      sessionStatus: null,
      sessionLoading: false,
      sessionValidateResult: null,
      sessionActionStatus: null,
      sessionActionMessage: '',
      pricing: null,
      pricingDefaults: null,
      pricingLoading: false,
      pricingSaving: false,
      pricingMessage: '',
      pricingError: false
    }
  },
  methods: {
    // The API stores multipliers (1.45); the form edits markup percent (45).
    pricingToForm(cfg) {
      return {
        flatFeeEur: cfg.flatFeeEur,
        tiers: cfg.tiers.map(t => ({
          maxEur: t.maxEur === null || t.maxEur === undefined ? '' : t.maxEur,
          markupPercent: Math.round((t.multiplier - 1) * 10000) / 100
        }))
      };
    },
    async loadPricing() {
      this.pricingLoading = true;
      try {
        const r = await fetch('/api/admin/pricing', { credentials: 'include' });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Failed to load pricing');
        this.pricing = this.pricingToForm(data.config);
        this.pricingDefaults = data.defaults;
      } catch (e) {
        this.pricingError = true;
        this.pricingMessage = e.message;
      } finally {
        this.pricingLoading = false;
      }
    },
    addPricingTier() {
      const tiers = this.pricing.tiers;
      const last = tiers[tiers.length - 1];
      // Close the open-ended last tier so the new one can take over above it.
      if (last && (last.maxEur === '' || last.maxEur === null)) {
        const prev = tiers[tiers.length - 2];
        last.maxEur = (prev ? Number(prev.maxEur) : 0) + 100;
      }
      tiers.push({ maxEur: '', markupPercent: last ? last.markupPercent : 0 });
    },
    resetPricingToDefaults() {
      if (this.pricingDefaults) this.pricing = this.pricingToForm(this.pricingDefaults);
    },
    async savePricing() {
      this.pricingSaving = true;
      this.pricingMessage = '';
      this.pricingError = false;
      try {
        const body = {
          flatFeeEur: this.pricing.flatFeeEur,
          tiers: this.pricing.tiers.map(t => ({
            maxEur: t.maxEur === '' || t.maxEur === null ? null : Number(t.maxEur),
            multiplier: Math.round((1 + Number(t.markupPercent) / 100) * 10000) / 10000
          }))
        };
        const r = await fetch('/api/admin/pricing', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body)
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Failed to save pricing');
        this.pricing = this.pricingToForm(data.config);
        this.pricingMessage = data.applied
          ? 'Saved and applied'
          : 'Saved, but the scraper service did not respond — it will pick this up within a minute';
      } catch (e) {
        this.pricingError = true;
        this.pricingMessage = e.message;
      } finally {
        this.pricingSaving = false;
      }
    },
    async loadSessionStatus() {
      this.sessionLoading = true;
      this.sessionValidateResult = null;
      try {
        const r = await fetch('/api/admin/sessions/status', { credentials: 'include' });
        this.sessionStatus = await r.json();
      } catch (e) {
        this.sessionStatus = null;
      } finally {
        this.sessionLoading = false;
      }
    },

    async validateSession() {
      this.sessionLoading = true;
      try {
        const r = await fetch('/api/admin/sessions/validate', { method: 'POST', credentials: 'include' });
        this.sessionValidateResult = await r.json();
      } catch (e) {
        this.sessionValidateResult = { ok: false, reason: e.message };
      } finally {
        this.sessionLoading = false;
      }
    },

    async sessionAction(endpoint, label) {
      this.sessionActionStatus = 'loading';
      this.sessionActionMessage = '';
      try {
        const r = await fetch(`/api/admin/sessions/${endpoint}`, { method: 'POST', credentials: 'include' });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Failed');
        this.sessionActionStatus = 'success';
        this.sessionActionMessage = label + ' done';
        await this.loadSessionStatus();
      } catch (e) {
        this.sessionActionStatus = 'error';
        this.sessionActionMessage = e.message;
      } finally {
        setTimeout(() => { this.sessionActionStatus = null; this.sessionActionMessage = ''; }, 4000);
      }
    },

    async clearCategoryCache() {
      this.cacheClearStatus = 'loading';
      this.cacheClearMessage = '';
      try {
        const response = await fetch('/api/admin/clear-collection-cache', {
          method: 'POST',
          credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Request failed');
        this.cacheClearStatus = 'success';
        this.cacheClearMessage = data.message || 'Cache cleared successfully';
      } catch (err) {
        this.cacheClearStatus = 'error';
        this.cacheClearMessage = err.message || 'Failed to clear cache';
      } finally {
        setTimeout(() => { this.cacheClearStatus = null; this.cacheClearMessage = ''; }, 4000);
      }
    },

    async loadHomepageData() {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/homepage`);
        if (!response.ok) {
          throw new Error('Failed to load homepage data');
        }
        const data = await response.json();
        
        // Ensure image URLs are complete
        this.slides = (data.slides || []).map(slide => ({
          ...slide,
          image: slide.image && !slide.image.startsWith('http') ? `${config.apiBaseUrl}${slide.image}` : slide.image
        }));
        
        this.categories = (data.categories || []).map(category => ({
          ...category,
          image: category.image && !category.image.startsWith('http') ? `${config.apiBaseUrl}${category.image}` : category.image
        }));

        const rawCarousel = data.productCarousel || {};
        const rawItems = Array.isArray(rawCarousel.items) ? rawCarousel.items : [];
        this.productCarousel = {
          enabled: typeof rawCarousel.enabled === 'boolean' ? rawCarousel.enabled : rawItems.length > 0,
          speedPxPerSec: Number.isFinite(Number(rawCarousel.speedPxPerSec)) && Number(rawCarousel.speedPxPerSec) > 0
            ? Number(rawCarousel.speedPxPerSec)
            : 55,
          items: rawItems.map(item => ({
            title: item?.title || '',
            price: item?.price || '',
            link: item?.link || '',
            image: item?.image && !item.image.startsWith('http') ? `${config.apiBaseUrl}${item.image}` : (item?.image || '')
          }))
        };
      } catch (error) {
        console.error('Error loading homepage data:', error);
      }
    },
    async reloadWords() {
      await this.loadNoTranslationWords();
    },
    async loadNoTranslationWords() {
      try {
        this.wordsLoading = true;
        const res = await fetch('/api/admin/no-translation-words', {
          method: 'GET',
          credentials: 'include'
        });
        if (!res.ok) throw new Error('Failed to load words');
        const data = await res.json();
        this.wordsCsv = (data && data.csv) ? data.csv : (Array.isArray(data?.words) ? data.words.join('\n') : '');
      } catch (e) {
        console.error('Error loading non-translation words:', e);
        alert('Failed to load non-translation words.');
      } finally {
        this.wordsLoading = false;
      }
    },
    async saveWords() {
      try {
        this.saveWordsLoading = true;
        const res = await fetch('/api/admin/no-translation-words', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ csv: this.wordsCsv })
        });
        if (!res.ok) throw new Error('Failed to save words');
        const data = await res.json();
        this.wordsCsv = data.csv || this.wordsCsv;
        alert('Non-translation words saved.');
      } catch (e) {
        console.error('Error saving non-translation words:', e);
        alert('Failed to save non-translation words.');
      } finally {
        this.saveWordsLoading = false;
      }
    },
    async saveChanges() {
      try {
        this.loading = true;
        // Remove the full URL before saving, keep only the path
        const slidesToSave = this.slides.map(slide => ({
          ...slide,
          image: slide.image ? slide.image.replace(config.apiBaseUrl, '') : ''
        }));

        const categoriesToSave = this.categories.map(category => ({
          ...category,
          image: category.image ? category.image.replace(config.apiBaseUrl, '') : '',
          hidden: !!category.hidden
        }));

        const productCarouselToSave = {
          enabled: !!this.productCarousel.enabled,
          speedPxPerSec: Number.isFinite(Number(this.productCarousel.speedPxPerSec)) && Number(this.productCarousel.speedPxPerSec) > 0
            ? Number(this.productCarousel.speedPxPerSec)
            : 55,
          items: (Array.isArray(this.productCarousel.items) ? this.productCarousel.items : []).map((item) => ({
            title: item?.title || '',
            price: item?.price || '',
            link: item?.link || '',
            image: item?.image ? item.image.replace(config.apiBaseUrl, '') : ''
          }))
        };

        const response = await fetch(`${config.apiBaseUrl}/api/homepage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            slides: slidesToSave,
            categories: categoriesToSave,
            productCarousel: productCarouselToSave
          })
        });

        if (!response.ok) {
          throw new Error('Failed to save changes');
        }

        alert('Changes saved successfully!');
      } catch (error) {
        console.error('Error saving changes:', error);
        alert('Failed to save changes. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    async handleImageUpload(event, type, index) {
      try {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch(`${config.apiBaseUrl}/api/upload`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }

        const data = await response.json();
        const imageUrl = `${config.apiBaseUrl}${data.imageUrl}`;
        
        if (type === 'slide') {
          this.slides[index].image = imageUrl;
        } else if (type === 'category') {
          this.categories[index].image = imageUrl;
        } else if (type === 'productCarousel') {
          this.productCarousel.items[index].image = imageUrl;
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      }
    },
    addSlide() {
      this.slides.push({
        title: '',
        description: '',
        image: '',
        link: ''
      });
    },
    removeSlide(index) {
      this.slides.splice(index, 1);
    },
    addCategory() {
      this.categories.push({
        name: '',
        description: '',
        image: '',
        hidden: false
      });
    },
    removeCategory(index) {
      this.categories.splice(index, 1);
    },
    moveCategory(index, direction) {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= this.categories.length) return;

      const updatedCategories = [...this.categories];
      const [movedCategory] = updatedCategories.splice(index, 1);
      updatedCategories.splice(targetIndex, 0, movedCategory);
      this.categories = updatedCategories;
    },
    addCarouselProductToEnd() {
      this.productCarousel.items.push({
        title: '',
        price: '',
        image: '',
        link: ''
      });
    },
    addCarouselProductToStart() {
      this.productCarousel.items.unshift({
        title: '',
        price: '',
        image: '',
        link: ''
      });
    },
    removeCarouselProduct(index) {
      this.productCarousel.items.splice(index, 1);
    },
    moveCarouselProduct(index, direction) {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= this.productCarousel.items.length) return;

      const updatedItems = [...this.productCarousel.items];
      const [movedItem] = updatedItems.splice(index, 1);
      updatedItems.splice(targetIndex, 0, movedItem);
      this.productCarousel.items = updatedItems;
    }
  },
  mounted() {
    this.loadHomepageData();
    this.loadNoTranslationWords();
    this.loadPricing();
  }
}
</script>

<style scoped>
.admin-home {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.section {
  margin-bottom: 40px;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h1 {
  margin-bottom: 30px;
  color: #333;
}

h2 {
  margin-bottom: 20px;
  color: #444;
}

.upload-guidance {
  margin: -6px 0 14px;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.45;
}

.upload-guidance strong {
  color: #1f2937;
  font-weight: 700;
}

.slider-management, .categories-management, .carousel-management {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.slider-item, .category-item, .carousel-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 15px;
  background: #f8f8f8;
  border-radius: 4px;
  margin-bottom: 10px;
}

.slider-item img, .category-item img {
  width: 100px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.slider-info, .category-info, .carousel-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.carousel-settings-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-bottom: 14px;
}

.switch-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
}

.speed-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #374151;
  font-weight: 600;
}

.speed-label input {
  width: 96px;
}

.category-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.category-order-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #e9f2ff;
  color: #174ea6;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.reorder-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.reorder-btn {
  background: #1f2937;
  font-size: 13px;
}

.reorder-btn:hover:not(:disabled) {
  background: #111827;
}

.reorder-btn:disabled {
  background: #cbd5e1;
  color: #64748b;
  cursor: not-allowed;
}

.hidden-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  user-select: none;
}

.hidden-toggle input[type="checkbox"] {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #dc3545;
}

input {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

button {
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  background: #0056b3;
}

.delete-btn {
  background: #dc3545;
}

@media (max-width: 768px) {
  .category-item,
  .carousel-item {
    flex-direction: column;
    align-items: stretch;
  }

  .category-meta-row {
    align-items: flex-start;
    flex-direction: column;
  }
}

.delete-btn:hover {
  background: #c82333;
}

.save-btn {
  background: #28a745;
  padding: 12px 24px;
  font-size: 16px;
}

.save-btn:hover {
  background: #218838;
}

.save-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.add-btn {
  background: #28a745;
  padding: 12px 24px;
  font-size: 16px;
}

.add-btn:hover {
  background: #218838;
}

.add-btn--start {
  background: #6c757d;
}

.add-btn--start:hover {
  background: #5a6268;
}

.carousel-add-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.image-preview {
  width: 200px;
  height: 120px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  color: #999;
  font-size: 0.9rem;
}

input[type="file"] {
  margin-top: 10px;
  width: 100%;
}

.csv-textarea {
  width: 100%;
  min-height: 220px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  line-height: 1.4;
  resize: vertical;
}
</style> 